
import React from 'react';
import WorkflowStep from './WorkflowStep';
import { WorkflowStep as WorkflowStepType } from '../types';

interface WorkflowProgressProps {
  steps: WorkflowStepType[];
  currentStep: number;
  onViewResult: (stepId: number) => void;
}

const WorkflowProgress: React.FC<WorkflowProgressProps> = ({
  steps,
  currentStep,
  onViewResult,
}) => {
  // Defensive check to ensure steps is always an array
  const safeSteps = Array.isArray(steps) ? steps : [];
  
  if (safeSteps.length === 0) {
    console.warn('WorkflowProgress received empty or invalid steps array');
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="text-center">
          <h2 className="text-lg font-bold text-neutral-700">
            Loading Analysis Steps...
          </h2>
          <p className="text-sm text-neutral-500">Please wait while we initialize your campaign analysis.</p>
        </div>
      </div>
    );
  }

  const completedSteps = safeSteps.filter(step => step?.status === 'completed').length;
  const progress = Math.round((completedSteps / safeSteps.length) * 100);

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'research':
        return 'bg-indigo-600';
      case 'aspiration':
        return 'bg-blue-600';
      case 'where-to-play':
        return 'bg-green-600';
      case 'how-to-win':
        return 'bg-purple-600';
      case 'messaging':
        return 'bg-pink-600';
      case 'capabilities':
        return 'bg-orange-600';
      case 'management':
        return 'bg-red-600';
      default:
        return 'bg-neutral-600';
    }
  };

  const getStageTitle = (stage: string) => {
    switch (stage) {
      case 'research':
        return 'Research & Intelligence';
      case 'aspiration':
        return 'Winning Aspiration';
      case 'where-to-play':
        return 'Where to Play';
      case 'how-to-win':
        return 'How to Win';
      case 'messaging':
        return 'Messaging Guide';
      case 'capabilities':
        return 'Core Capabilities';
      case 'management':
        return 'Management Systems';
      default:
        return 'Analysis';
    }
  };

  // Group steps by stage
  const stageGroups = safeSteps.reduce((acc, step) => {
    if (!step || !step.stage) {
      console.warn('Invalid step found:', step);
      return acc;
    }
    
    if (!acc[step.stage]) {
      acc[step.stage] = [];
    }
    acc[step.stage].push(step);
    return acc;
  }, {} as Record<string, WorkflowStepType[]>);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="text-center mb-6">
        <div className="border-2 border-red-600 inline-block p-3 mb-4">
          <h2 className="text-lg font-bold text-red-700 tracking-wider">
            STRATEGIC ANALYSIS
            <br />
            PROGRESS
          </h2>
        </div>
        <div className="text-sm text-neutral-600 font-medium">
          {completedSteps}/{safeSteps.length} steps completed ({progress}%)
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-neutral-100 rounded-full mb-8 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="space-y-8">
        {Object.entries(stageGroups).map(([stage, stageSteps]) => (
          <div key={stage} className="border-l-4 border-red-200 pl-6">
            <div className="flex items-center mb-4">
              <div className={`w-3 h-3 rounded-full ${getStageColor(stage)} mr-3`}></div>
              <h3 className="text-lg font-bold text-neutral-800 uppercase tracking-wide">
                {getStageTitle(stage)}
              </h3>
            </div>
            
            <div className="space-y-4">
              {stageSteps.map((step) => (
                <WorkflowStep
                  key={step.id}
                  title={step.title}
                  description={step.description}
                  status={step.status}
                  current={currentStep === step.id}
                  onClick={step.status === 'completed' ? () => onViewResult(step.id) : undefined}
                  stepNumber={step.id}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowProgress;
